import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  TablePagination,
  Paper,
  Divider,
  Tooltip,
} from '@mui/material';

import {
  Receipt,
  CheckCircle,
  AttachMoney,
  ShoppingCart,
  FilterList,
  CalendarToday,
  Clear,
  Today,
  CalendarMonth,
  DateRange,
  Print,
  Close,
  Inventory,
  Download,
  WarningAmber,
} from '@mui/icons-material';

import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import IUser from '../../main/interfaces/IUser';
import IInvoice from '../../main/interfaces/IInvoice';
import IInvoiceRows from '../../main/interfaces/IInvoiceRows';
import './EmployeeInvoicess.css';
import BasicCli from '../../main/components/basicCli/BasicCli';
import { usePerformAction } from '../../main/store/stores/user/permission.store';
import useGetUser from '../../main/hooks/useGetUser';
import './PrintSummary.css';
import { useTranslation } from 'react-i18next';

interface ItemStatistics {
  productName: string;
  productId: number;
  totalQuantity: number;
  totalRevenue: number;
  unitPrice: number;
}

interface InvoiceFilterRequest {
  userId?: number | null;
  user?: number | null;
  closed?: boolean | null;
  status?: number | null;
  statusId?: number | null;
  invoiceStatus?: number | null;
  paid?: boolean | null;
  isPaid?: boolean | null;
  dateTo?: string | null;
  dateFrom?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  rangeKey?: string | null;
  // compatibility fields (some backend builds still read these names)
  startDate?: string | null;
  endDate?: string | null;
}

interface DailyCloseSummary {
  dateRange: string;
  items: Array<{
    productName: string;
    quantity: number;
    unitPrice: number;
    totalValue: number;
  }>;
  totals: {
    totalQuantity: number;
    totalValue: number;
    totalItems: number;
  };
  filteredInvoicesCount: number;
}

type PaidFilter = 0 | 1 | 2;
type QuickRangeKey = 'today' | 'this_week' | 'this_month' | '3_months' | '6_months' | '1_year' | 'custom';
const CLOSED_STATUS = 2;

const EmployeeInvoices: React.FC = () => {
  const user = useGetUser();

  // permissions (hooks thirren 1 here)
  const canCloseAll = usePerformAction('close-opened-by-user', true);
  // NOTE: canPrintReport nuk e pÃ«rdorim mÃ« pÃ«r tÃ« bllokuar butonin (sipas kÃ«rkesÃ«s)
  // const canPrintReport = usePerformAction('print-invoices-report', true);
  const { i18n } = useTranslation();
  const [users, setUsers] = useState<IUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null); // null = all users

  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);

  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  const [statusFilter, setStatusFilter] = useState<number | null>(null); // null = all
  const [paidFilter, setPaidFilter] = useState<PaidFilter>(2);

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateTo, setDateTo] = useState<string>(new Date().toISOString().split('T')[0]); // yyyy-mm-dd
  const [selectedRangeKey, setSelectedRangeKey] = useState<QuickRangeKey>('today');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [showCloseAllConfirm, setShowCloseAllConfirm] = useState(false);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [dailySummary, setDailySummary] = useState<DailyCloseSummary | null>(null);

  const currencySymbol = user?.companyCurrencySymbol ?? '';

  /* ===================== FORMATTERS ===================== */
  const formatCurrency = useCallback((value: number) => {
    return new Intl.NumberFormat('sq-AL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }, []);

  const formatDateForDisplay = useCallback((dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('sq-AL');
    } catch {
      return dateString;
    }
  }, []);

  const formatDate = useCallback((d?: Date | string) => (d ? new Date(d).toLocaleDateString('sq-AL') : '—'), []);

  const formatDateForInput = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const formatInvoiceId = useCallback((id?: number) => {
    if (!id) return '';

    const displayId = Math.abs(id);
    const playgroundOffset = 1000000000;

    return (displayId > playgroundOffset ? displayId - playgroundOffset : displayId).toString();
  }, []);

  const getStatusInfo = useCallback(
    (status?: number) => {
      switch (status) {
        case 1:
          return { label: i18n.t('generic.invoice.Open', { defaultValue: 'Open' }), color: 'open' as const };
        case 2:
          return { label: i18n.t('generic.invoice.Closed', { defaultValue: 'Closed' }), color: 'closed' as const };
        case 3:
          return { label: i18n.t('generic.invoice.Cancelled', { defaultValue: 'Cancelled' }), color: 'cancelled' as const };
        default:
          return { label: 'â€”', color: 'open' as const };
      }
    },
    [i18n],
  );

  /* ===================== DERIVED FILTERS ===================== */
  const filteredInvoices = useMemo(() => {
    const result = [...(invoices ?? [])].filter((inv) => {
      if (paidFilter === 1) return inv.paid === true;
      if (paidFilter === 2) return inv.paid !== true;
      return true;
    });

    result.sort((a, b) => new Date(b.created || '').getTime() - new Date(a.created || '').getTime());
    return result;
  }, [invoices, paidFilter]);

  const closedInvoicesForReport = useMemo(() => {
    const result = (invoices ?? []).filter((inv) => {
      if (inv.status !== CLOSED_STATUS) return false;
      return true;
    });

    result.sort((a, b) => new Date(b.created || '').getTime() - new Date(a.created || '').getTime());
    return result;
  }, [invoices]);

  const paginatedInvoices = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredInvoices.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredInvoices, page, rowsPerPage]);

  const itemStatistics = useMemo<ItemStatistics[]>(() => {
    const map = new Map<number, ItemStatistics>();

    filteredInvoices.forEach((inv) => {
      inv.invoiceRows?.forEach((row: IInvoiceRows) => {
        const productId = row.productId || (typeof row.product === 'object' ? row.product?.id : (row.product as any));
        if (!productId) return;

        const name = typeof row.product === 'object' ? row.product?.name || `Produkt #${productId}` : `Produkt #${productId}`;

        const quantity = row.quantity || 0;
        const revenue = quantity * (row.price || 0);

        const existing = map.get(productId);
        if (existing) {
          existing.totalQuantity += quantity;
          existing.totalRevenue += revenue;
          existing.unitPrice = existing.totalRevenue / Math.max(1, existing.totalQuantity);
        } else {
          map.set(productId, {
            productId,
            productName: name,
            totalQuantity: quantity,
            totalRevenue: revenue,
            unitPrice: row.price || 0,
          });
        }
      });
    });

    return Array.from(map.values()).sort((a, b) => b.totalRevenue - a.totalRevenue);
  }, [filteredInvoices]);

  const totalStats = useMemo(() => {
    const totalAmount = filteredInvoices.reduce((s, i) => s + (i.price || 0), 0);
    const totalItems = filteredInvoices.reduce((s, i) => s + (i.invoiceRows?.reduce((r, row) => r + (row.quantity || 0), 0) || 0), 0);

    return {
      totalInvoices: filteredInvoices.length,
      totalAmount,
      totalItems,
      uniqueProducts: itemStatistics.length,
    };
  }, [filteredInvoices, itemStatistics.length]);

  const dateRangeText = useMemo(() => {
    const dateLabel = formatDateForDisplay(dateTo);
    switch (selectedRangeKey) {
      case 'today':
        return i18n.t('menu.Today', { defaultValue: 'Sot' });
      case 'this_week':
        return i18n.t('menu.ThisWeek', { defaultValue: 'Kjo Javë' });
      case 'this_month':
        return i18n.t('menu.ThisMonth', { defaultValue: 'Ky Muaj' });
      case '3_months':
        return i18n.t('invoice.last3Months', { defaultValue: '3 Muaj' });
      case '6_months':
        return i18n.t('invoice.last6Months', { defaultValue: '6 Muaj' });
      case '1_year':
        return i18n.t('invoice.last1Year', { defaultValue: '1 Vit' });
      default:
        return dateLabel;
    }
  }, [dateTo, selectedRangeKey, formatDateForDisplay, i18n]);

  /* ===================== API ===================== */
  const resolveRangeWindow = useCallback(
    (key: QuickRangeKey, endDateInput?: string) => {
      const endBase = endDateInput ? new Date(endDateInput) : new Date();
      const end = new Date(endBase);
      const start = new Date(endBase);

      if (key === 'today' || key === 'custom') {
        // single day window
      } else if (key === 'this_week') {
        const day = start.getDay();
        const diffToMonday = (day + 6) % 7;
        start.setDate(start.getDate() - diffToMonday);
      } else if (key === 'this_month') {
        start.setDate(1);
      } else if (key === '3_months') {
        start.setMonth(start.getMonth() - 3);
      } else if (key === '6_months') {
        start.setMonth(start.getMonth() - 6);
      } else if (key === '1_year') {
        start.setFullYear(start.getFullYear() - 1);
      }

      return {
        startDate: formatDateForInput(start),
        endDate: formatDateForInput(end),
      };
    },
    [formatDateForInput],
  );

  const buildInvoiceFilter = useCallback(
    (params?: { userId?: number | null; dateValue?: string; rangeKey?: QuickRangeKey; statusValue?: number | null }) => {
      const userIdToSend = params?.userId ?? selectedUserId;
      const dateToSend = params?.dateValue ?? dateTo;
      const statusToSend = params?.statusValue ?? statusFilter;
      const rangeToSend = params?.rangeKey ?? selectedRangeKey;
      const { startDate, endDate } = resolveRangeWindow(rangeToSend, dateToSend);
      const closedToSend = statusToSend === CLOSED_STATUS ? true : statusToSend === 1 ? false : null;

      return {
        userId: userIdToSend && userIdToSend > 0 ? userIdToSend : 0,
        user: userIdToSend && userIdToSend > 0 ? userIdToSend : 0,
        closed: closedToSend,
        status: statusToSend,
        statusId: statusToSend,
        invoiceStatus: statusToSend,
        paid: null,
        isPaid: null,
        dateTo: endDate,
        dateFrom: startDate,
        fromDate: startDate,
        toDate: endDate,
        rangeKey: rangeToSend,
        startDate,
        endDate,
      } as InvoiceFilterRequest;
    },
    [selectedUserId, dateTo, statusFilter, selectedRangeKey, resolveRangeWindow],
  );

  const requestInvoices = useCallback(async (filter: InvoiceFilterRequest): Promise<IInvoice[]> => {
    const response = await axiosInstance.post(ApiConstants.invoice.filterByUser, filter);
    const data: any[] = response.data ?? [];

    return data.map((inv: any) => ({
      id: inv.id,
      tableId: inv.tableId ?? 0,
      userId: inv.userId ?? 0,
      name: inv.name ?? '',
      price: Number(inv.price ?? 0),
      currentPrice: Number(inv.currentPrice ?? inv.price ?? 0),
      quantity: Number(inv.quantity ?? 0),
      companyId: inv.companyId ?? 0,
      created: inv.created,
      IsDelete: Boolean(inv.isDelete ?? inv.IsDelete),
      deleted: inv.deleted ?? null,
      paid: inv.paid === true,
      status: typeof inv.status === 'number' ? inv.status : null,
      invoiceRows: (inv.invoiceRows ?? []).map((row: any) => ({
        id: row.id,
        invoiceId: row.invoiceId,
        price: Number(row.price ?? 0),
        quantity: Number(row.quantity ?? 0),
        vatRate: row.vatRate,
        vatAmount: row.vatAmount,
        totalWithoutVat: row.totalWithoutVat,
        totalWithVat: row.totalWithVat,
        productId: row.productId,
        product: typeof row.product === 'object' && row.product ? { id: row.product.id, name: row.product.name } : row.product,
        companyId: row.companyId,
      })),
    })) as IInvoice[];
  }, []);

  const fetchUserInvoices = useCallback(
    async (
      options?: { userId?: number | null; dateValue?: string; rangeKey?: QuickRangeKey; statusValue?: number | null },
      config?: { updateState?: boolean; showLoading?: boolean; clearMessages?: boolean },
    ) => {
      const updateState = config?.updateState ?? true;
      const showLoading = config?.showLoading ?? true;
      const clearMessages = config?.clearMessages ?? true;

      try {
        if (showLoading) setLoadingInvoices(true);
        if (clearMessages) {
          setError('');
          setInfoMessage('');
        }
        if (updateState) setPage(0);

        const filter = buildInvoiceFilter(options);
        const compactData = await requestInvoices(filter);

        if (updateState) setInvoices(compactData);
        if (compactData.length === 0 && updateState) setInfoMessage(i18n.t('invoice.noInvoices'));
        return compactData;
      } catch (e: any) {
        console.error(e);
        const serverMessage = e.response?.data?.message || '';
        setError(i18n.t('errors.loadInvoice') + serverMessage);
        return [];
      } finally {
        if (showLoading) setLoadingInvoices(false);
      }
    },
    [buildInvoiceFilter, requestInvoices, i18n],
  );

  const fetchUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      setError('');
      setInfoMessage('');

      const response = await axiosInstance.get(ApiConstants.User.users);
      const activeUsers: IUser[] = (response.data ?? []).filter((u: IUser) => !u.isDelete);
      setUsers(activeUsers);
    } catch (e) {
      console.error(e);
      setError(i18n.t('errors.loadUsers'));
    } finally {
      setLoadingUsers(false);
    }
  }, [i18n]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchUserInvoices();
  }, [selectedUserId, statusFilter, dateTo, selectedRangeKey, fetchUserInvoices]);

  const closeAllInvoicesByUser = useCallback(async () => {
    if (!selectedUserId) return;

    try {
      setShowCloseAllConfirm(false);
      setLoadingInvoices(true);
      setError('');
      setInfoMessage('');
      const { startDate, endDate } = resolveRangeWindow(selectedRangeKey, dateTo);

      const response = await axiosInstance.post(
        ApiConstants.invoice.closeAllOpenedByUser + `?userId=${selectedUserId}&startDate=${startDate}&endDate=${endDate}`,
      );

      const closeUmbDates: string[] = [];
      const cursor = new Date(startDate);
      const lastDate = new Date(endDate);
      while (cursor <= lastDate) {
        closeUmbDates.push(formatDateForInput(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }

      await Promise.all(
        closeUmbDates.map((date) =>
          Promise.all([
            axiosInstance.post(ApiConstants.umb.closeInvoice, {
              userId: selectedUserId,
              date,
              closed: false,
            }),
            axiosInstance.post(ApiConstants.playground.closeInvoice, {
              userId: selectedUserId,
              date,
              closed: false,
            }),
          ]),
        ),
      );

      setInfoMessage(i18n.t('invoice.closedOpenInvoices', { count: response.data?.ClosedInvoices ?? 0 }));
      await fetchUserInvoices();
    } catch (e) {
      console.error(e);
      setError(i18n.t('errors.closeInvoices'));
    } finally {
      setLoadingInvoices(false);
    }
  }, [selectedUserId, dateTo, selectedRangeKey, fetchUserInvoices, formatDateForInput, i18n, resolveRangeWindow]);

  /* ===================== REPORT SUMMARY ===================== */
  const buildClosedInvoicesReportSummary = useCallback(
    (sourceInvoices: IInvoice[], overrideDateRange?: string): DailyCloseSummary | null => {
      if (!sourceInvoices || sourceInvoices.length === 0) return null;

      const itemsMap = new Map<number, { productName: string; quantity: number; unitPrice: number; totalValue: number }>();

      sourceInvoices.forEach((invoice) => {
        invoice.invoiceRows?.forEach((row: IInvoiceRows) => {
          const productId = row.productId || (typeof row.product === 'object' ? row.product?.id : (row.product as any));
          if (!productId) return;

          const productName = typeof row.product === 'object' ? row.product?.name || `Produkt #${productId}` : `Produkt #${productId}`;

          const quantity = row.quantity || 0;
          const unitPrice = row.price || 0;
          const totalValue = quantity * unitPrice;

          const existing = itemsMap.get(productId);
          if (existing) {
            existing.quantity += quantity;
            existing.totalValue += totalValue;
            existing.unitPrice = existing.totalValue / Math.max(1, existing.quantity);
          } else {
            itemsMap.set(productId, { productName, quantity, unitPrice, totalValue });
          }
        });
      });

      const items = Array.from(itemsMap.values()).sort((a, b) => b.totalValue - a.totalValue);

      const totals = {
        totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0),
        totalValue: items.reduce((sum, item) => sum + item.totalValue, 0),
        totalItems: items.length,
      };

      return {
        dateRange: overrideDateRange ?? dateRangeText,
        items,
        totals,
        filteredInvoicesCount: sourceInvoices.length,
      };
    },
    [dateRangeText],
  );

  const handlePrintClosedReport = useCallback(async () => {
    try {
      setLoadingReport(true);
      setError('');
      setInfoMessage('');

      if (closedInvoicesForReport.length === 0) {
        setInfoMessage(
          i18n.t('invoice.noClosedInvoicesForSelectedPeriod', {
            defaultValue: 'Nuk ka fatura closed per periudhen e zgjedhur.',
          }),
        );
        return;
      }

      const summary = buildClosedInvoicesReportSummary(closedInvoicesForReport);
      if (!summary) {
        setInfoMessage(i18n.t('invoice.summaryNotCreated'));
        return;
      }

      setDailySummary(summary);
      setShowSummaryDialog(true);

      setInfoMessage(
        i18n.t('invoice.reportCreatedForFiltered', {
          defaultValue: 'Raporti u krijua për {count} fatura.',
          count: summary.filteredInvoicesCount,
        }),
      );
    } catch (e: any) {
      console.error(e);
      const serverMessage = e.response?.data?.message || '';
      setError(
        i18n.t('errors.reportError', {
          defaultValue: 'Gabim gjatë krijimit të raportit: {message}',
          message: serverMessage,
        }),
      );
    } finally {
      setLoadingReport(false);
    }
  }, [closedInvoicesForReport, buildClosedInvoicesReportSummary, i18n]);

  /* ===================== PRINT / PDF ===================== */
  const printSummary = useCallback(() => {
    const el = document.getElementById('daily-summary-content');
    if (!el) return;

    const w = window.open('', '_blank', 'noopener,noreferrer,width=900,height=700');
    if (!w) return;

    const rootStyles = getComputedStyle(document.documentElement);
    const cssVars = [
      '--bg',
      '--surface',
      '--surface-2',
      '--surface-3',
      '--text',
      '--text-2',
      '--muted',
      '--border',
      '--divider',
      '--primary',
      '--primary-contrast',
      '--success',
      '--warning',
      '--danger',
      '--shadow-sm',
      '--shadow',
      '--r-sm',
      '--r-md',
      '--r-lg',
    ]
      .map((v) => `${v}:${rootStyles.getPropertyValue(v)};`)
      .join('');

    w.document.write(`
      <html>
        <head>
          <title>Print</title>
          <style>
            :root{${cssVars}}
            body{font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial; padding: 16px; color: var(--text); background: var(--bg);}
            .printWrap{background: var(--surface); border: 1px solid var(--border); border-radius: var(--r-lg); overflow:hidden;}
            @media print { body{padding:0;background:#fff;} .printWrap{border:none;border-radius:0;} }
          </style>
        </head>
        <body>
          <div class="printWrap">${el.innerHTML}</div>
          <script>
            window.onload = () => { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `);
    w.document.close();
  }, []);

  const downloadSummaryPdf = useCallback(() => {
    const el = document.getElementById('daily-summary-content');
    if (!el || !dailySummary) return;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;

    const html = `
  <!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <title>Sold items summary</title>
      <style>
        @page { size: A4; margin: 10mm; }
        body { font-family: Arial, sans-serif; color:#111; background:#fff; margin:0; }
        .wrap { padding: 10mm; }
        table { width:100%; border-collapse: collapse; }
        th, td { border:1px solid #ddd; padding:6px; font-size:12px; }
        th { font-weight:700; background:#f5f5f5; }
        .MuiTableCell-root { border:1px solid #ddd !important; } /* just in case */
        .noPrint { display:none !important; }
      </style>
    </head>
    <body>
      <div class="wrap">
        ${el.outerHTML}
      </div>
      <script>
        setTimeout(() => window.print(), 300);
      </script>
    </body>
  </html>`;

    w.document.open();
    w.document.write(html);
    w.document.close();
  }, [dailySummary]);

  const openSummaryPdfPreview = useCallback(
    (summary: DailyCloseSummary) => {
      const w = window.open('', '_blank', 'width=900,height=700');
      if (!w) return;

      const rowsHtml = summary.items
        .map(
          (item, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>${item.productName}</td>
              <td style="text-align:right">${item.quantity}</td>
              <td style="text-align:right">${formatCurrency(item.unitPrice)}</td>
              <td style="text-align:right">${formatCurrency(item.totalValue)}</td>
            </tr>
          `,
        )
        .join('');

      const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Invoice Report</title>
          <style>
            @page { size: A4; margin: 10mm; }
            body { font-family: Arial, sans-serif; color:#111; background:#fff; margin:0; }
            .wrap { padding: 10mm; }
            .meta { margin-bottom: 10px; font-size: 13px; }
            table { width:100%; border-collapse: collapse; }
            th, td { border:1px solid #ddd; padding:6px; font-size:12px; }
            th { font-weight:700; background:#f5f5f5; }
            .actions { margin-top: 12px; display:flex; gap:8px; }
            button { padding:8px 12px; border:1px solid #bbb; background:#fff; cursor:pointer; }
            @media print { .actions { display:none; } }
          </style>
        </head>
        <body>
          <div class="wrap">
            <div class="meta"><strong>Period:</strong> ${summary.dateRange}</div>
            <div class="meta"><strong>Invoices:</strong> ${summary.filteredInvoicesCount}</div>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item</th>
                  <th style="text-align:right">Qty</th>
                  <th style="text-align:right">Unit Price</th>
                  <th style="text-align:right">Total</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
                <tr>
                  <td colspan="2"><strong>Total</strong></td>
                  <td style="text-align:right"><strong>${summary.totals.totalQuantity}</strong></td>
                  <td style="text-align:right"></td>
                  <td style="text-align:right"><strong>${formatCurrency(summary.totals.totalValue)}</strong></td>
                </tr>
              </tbody>
            </table>
            <div class="actions">
              <button onclick="window.print()">Open PDF / Print</button>
            </div>
          </div>
        </body>
      </html>`;

      w.document.open();
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 300);
    },
    [formatCurrency],
  );

  /* ===================== UI HANDLERS ===================== */
  const handleUserChange = useCallback((id?: number | null) => {
    setSelectedUserId(id ?? null);
    setPage(0);
  }, []);

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleApplyDateFilter = useCallback(() => {
    setShowDatePicker(false);
    setSelectedRangeKey('custom');
    setPage(0);
    fetchUserInvoices();
  }, [fetchUserInvoices]);

  const handleQuickRangeSelect = useCallback(
    async (key: QuickRangeKey, downloadFile = false) => {
      const { startDate, endDate } = resolveRangeWindow(key);
      setShowDatePicker(false);

      if (downloadFile) {
        try {
          setLoadingReport(true);
          const reportInvoices = await fetchUserInvoices(
            { dateValue: endDate, rangeKey: key },
            { updateState: false, showLoading: false, clearMessages: false },
          );
          const closedForReport = reportInvoices.filter((inv) => inv.status === CLOSED_STATUS);
          const rangeLabel = `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`;
          const summary = buildClosedInvoicesReportSummary(closedForReport, rangeLabel);
          if (!summary) {
            setInfoMessage(i18n.t('invoice.noClosedInvoicesForSelectedPeriod', { defaultValue: 'Nuk ka fatura për këtë periudhë.' }));
            return;
          }
          setDailySummary(summary);
          setShowSummaryDialog(true);
          openSummaryPdfPreview(summary);
          setInfoMessage(i18n.t('invoice.reportDownloaded', { defaultValue: 'Raporti u shkarkua me sukses.' }));
        } catch (e: any) {
          const serverMessage = e.response?.data?.message || '';
          setError(
            i18n.t('errors.reportError', {
              defaultValue: 'Gabim gjatë shkarkimit të raportit: {message}',
              message: serverMessage,
            }),
          );
        } finally {
          setLoadingReport(false);
        }
        return;
      }

      setDateTo(endDate);
      setSelectedRangeKey(key);
      await fetchUserInvoices({ dateValue: endDate, rangeKey: key });
    },
    [resolveRangeWindow, fetchUserInvoices, formatDateForDisplay, buildClosedInvoicesReportSummary, openSummaryPdfPreview, i18n],
  );

  const handleClearDateFilter = useCallback(() => {
    const today = formatDateForInput(new Date());
    setDateTo(today);
    setSelectedRangeKey('today');
    setPage(0);
    fetchUserInvoices({ dateValue: today, rangeKey: 'today' });
  }, [fetchUserInvoices, formatDateForInput]);

  /* ===================== RENDER ===================== */
  return (
    <BasicCli>
      <div className="eiPage" style={{ ['--ei-currency' as any]: `"${currencySymbol}"` }}>
        {/* Header */}
        <div className="eiHeader eiCard">
          <div className="eiHeaderText">
            <Typography variant="h1" className="eiTitle">
              {i18n.t('invoice.InvoicesReportByUser')}
            </Typography>
            <Typography variant="body1" className="eiSubtitle">
              {i18n.t('invoice.InvoicesManagementDescription')}
            </Typography>
          </div>
          <Receipt className="eiHeaderIcon" />
        </div>

        {/* Alerts */}
        {(error || infoMessage) && (
          <div className="eiAlerts">
            {error && <Alert severity="error">{error}</Alert>}
            {!error && infoMessage && <Alert severity="info">{infoMessage}</Alert>}
          </div>
        )}

        {/* Stats */}
        <div className="eiStats">
          <div className="eiStat eiCard">
            <div className="eiStatTop">
              <Typography className="eiStatLabel">{i18n.t('invoice.FilteredInvoices')}</Typography>
              <Receipt className="eiStatIcon" />
            </div>
            <div className="eiStatValue">{totalStats.totalInvoices}</div>
            <div className="eiStatHint">{i18n.t('invoice.InvoicesForPeriod')}</div>
          </div>

          <div className="eiStat eiCard eiStatPrimary">
            <div className="eiStatTop">
              <Typography className="eiStatLabel">{i18n.t('invoice.TotalValue')}</Typography>
              <AttachMoney className="eiStatIcon" />
            </div>
            <div className="eiStatValue">{formatCurrency(totalStats.totalAmount)}</div>
            <div className="eiStatHint">{i18n.t('invoice.TotalAmount')}</div>
          </div>

          <div className="eiStat eiCard">
            <div className="eiStatTop">
              <Typography className="eiStatLabel">{i18n.t('invoice.SoldItems')}</Typography>
              <ShoppingCart className="eiStatIcon" />
            </div>
            <div className="eiStatValue">{totalStats.totalItems}</div>
            <div className="eiStatHint">{i18n.t('invoice.ItemsForPeriod')}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="eiFilters eiCard">
          <div className="eiFiltersHead">
            <Typography className="eiFiltersTitle">
              <FilterList fontSize="small" /> {i18n.t('invoice.FilterBy')}
            </Typography>
          </div>

          <div className="eiFiltersGrid">
            <div className="eiField">
              <div className="eiLabel">{i18n.t('invoice.User')}</div>
              <FormControl fullWidth disabled={loadingUsers} size="small">
                <InputLabel>{i18n.t('invoice.SelectUser')}</InputLabel>
                <Select
                  value={selectedUserId ?? 0}
                  label={i18n.t('invoice.SelectUser')}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    handleUserChange(v === 0 ? null : v);
                  }}
                >
                  <MenuItem value={0}>{i18n.t('invoice.AllUsers', { defaultValue: 'All Users' })}</MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.firstName} {u.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* <div className="eiField">
              <div className="eiLabel">{i18n.t('invoice.Status')}</div>
              <FormControl fullWidth size="small">
                <InputLabel>{i18n.t('invoice.InvoiceStatus')}</InputLabel>
                <Select
                  value={statusFilter ?? ''}
                  label="Statusi i fatures"
                  onChange={(e) => {
                    setStatusFilter(e.target.value === '' ? null : Number(e.target.value));
                    setPage(0);
                  }}
                >
                  <MenuItem value="">{i18n.t('invoice.All')}</MenuItem>
                  <MenuItem value={1}>{i18n.t('invoice.Open')}</MenuItem>
                  <MenuItem value={2}>{i18n.t('invoice.Closed')}</MenuItem>
                  <MenuItem value={3}>{i18n.t('invoice.Cancelled')}</MenuItem>
                </Select>
              </FormControl>
            </div> */}

            <div className="eiField">
              <div className="eiLabel">{i18n.t('invoice.SubmissionStatus', { defaultValue: 'Submission status' })}</div>
              <FormControl fullWidth size="small">
                <InputLabel>{i18n.t('invoice.SubmissionStatus', { defaultValue: 'Submission status' })}</InputLabel>
                <Select
                  value={paidFilter}
                  label={i18n.t('invoice.SubmissionStatus', { defaultValue: 'Submission status' })}
                  onChange={(e) => {
                    setPaidFilter(Number(e.target.value) as PaidFilter);
                    setPage(0);
                  }}
                >
                  <MenuItem value={0}>{i18n.t('invoice.All', { defaultValue: 'All' })}</MenuItem>
                  <MenuItem value={2}>{i18n.t('invoice.NotSubmitted', { defaultValue: 'Not Submitted' })}</MenuItem>
                  <MenuItem value={1}>{i18n.t('invoice.Submitted', { defaultValue: 'Submitted' })}</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div className="eiField">
              <div className="eiLabel">{i18n.t('generic.date')}</div>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setShowDatePicker(true)}
                className={`eiDateBtn ${dateTo ? 'isActive' : ''}`}
                startIcon={<CalendarToday sx={{ fontSize: '1rem' }} />}
                endIcon={
                  dateTo ? (
                    <IconButton
                      size="small"
                      className="eiDateClear"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearDateFilter();
                      }}
                    >
                      <Clear sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  ) : null
                }
              >
                {dateRangeText}
              </Button>
            </div>

            <div className="eiField">
              <div className="eiLabel">{i18n.t('table.actions')}</div>
              <Tooltip
                title={
                  !canCloseAll
                    ? i18n.t('errors.noPermission', { defaultValue: 'Nuk keni leje për këtë veprim' })
                    : i18n.t('invoice.CloseAllOpenInvoices')
                }
              >
                <span>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => setShowCloseAllConfirm(true)}
                    disabled={!canCloseAll || loadingInvoices || !selectedUserId}
                    startIcon={<CheckCircle />}
                    size="small"
                    className="eiBtn eiBtnSuccess"
                  >
                    {i18n.t('invoice.CloseAll')}
                  </Button>
                </span>
              </Tooltip>
            </div>

            <div className="eiField">
              <div className="eiLabel">{i18n.t('invoice.Reports', { defaultValue: 'Raporte' })}</div>
              <Tooltip title={i18n.t('invoice.PrintClosedInvoicesReport', { defaultValue: 'Printo raportin e faturave të mbyllura' })}>
                <span>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handlePrintClosedReport}
                    disabled={loadingReport || closedInvoicesForReport.length === 0}
                    startIcon={<Print />}
                    size="small"
                    className="eiBtn eiBtnDanger"
                  >
                    {i18n.t('invoice.PrintReport', { defaultValue: 'Printo Raport' })}
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Close All Confirmation Dialog */}
        <Dialog
          open={showCloseAllConfirm}
          onClose={() => setShowCloseAllConfirm(false)}
          maxWidth="xs"
          fullWidth
          className="eiResponsiveDialog"
          classes={{ paper: 'white-dialog eiResponsiveDialogPaper' }}
        >
          <DialogTitle className="eiDialogTitle">
            <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmber color="warning" />
              {i18n.t('generic.confirmation', { defaultValue: 'Konfirmim' })}
            </Typography>
          </DialogTitle>

          <DialogContent className="eiDialogBody">
            <Typography sx={{ pt: 2, color: '#1e293b' }}>
              {i18n.t('invoice.confirmCloseAllOpenInvoices', {
                defaultValue: "A je i sigurt qe do t'i mbyllesh te gjitha faturat e hapura?",
              })}
            </Typography>
          </DialogContent>

          <DialogActions className="eiDialogActions">
            <Button onClick={() => setShowCloseAllConfirm(false)} disabled={loadingInvoices}>
              {i18n.t('generic.cancel', { defaultValue: 'Anulo' })}
            </Button>
            <Button onClick={closeAllInvoicesByUser} variant="contained" color="success" disabled={loadingInvoices} startIcon={<CheckCircle />}>
              {i18n.t('invoice.CloseAll', { defaultValue: 'Mbyll te gjitha' })}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Date Picker Dialog */}
        <Dialog
          open={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          maxWidth="sm"
          fullWidth
          className="eiResponsiveDialog"
          classes={{ paper: 'white-dialog eiResponsiveDialogPaper' }}
        >
          <DialogTitle className="eiDialogTitle">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              <DateRange sx={{ mr: 1, verticalAlign: 'middle' }} />
              {i18n.t('invoice.SelectDate', { defaultValue: 'Zgjidh daten' })}
            </Typography>
          </DialogTitle>

          <DialogContent className="eiDialogBody">
            <div className="eiDateGrid">
              <div className="eiDateRow">
                <CalendarMonth className="eiDateIcon" />
                <TextField
                  label={i18n.t('generic.date')}
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setSelectedRangeKey('custom');
                  }}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </div>

              <div className="eiQuickBtns">
                <Button variant="outlined" startIcon={<Today />} onClick={() => handleQuickRangeSelect('today')}>
                  {i18n.t('menu.Today', { defaultValue: 'Sot' })}
                </Button>
                <Button variant="outlined" onClick={() => handleQuickRangeSelect('this_week')}>
                  {i18n.t('menu.ThisWeek', { defaultValue: 'Kjo Javë' })}
                </Button>
                <Button variant="outlined" onClick={() => handleQuickRangeSelect('this_month')}>
                  {i18n.t('menu.ThisMonth', { defaultValue: 'Ky Muaj' })}
                </Button>
                <Button variant="outlined" onClick={() => handleQuickRangeSelect('3_months', true)}>
                  {i18n.t('invoice.last3Months', { defaultValue: '3 muajt e fundit' })}
                </Button>
                <Button variant="outlined" onClick={() => handleQuickRangeSelect('6_months', true)}>
                  {i18n.t('invoice.last6Months', { defaultValue: '6 muajt e fundit' })}
                </Button>
                <Button variant="outlined" onClick={() => handleQuickRangeSelect('1_year', true)}>
                  {i18n.t('invoice.last1Year', { defaultValue: '1 viti i fundit' })}
                </Button>
              </div>
            </div>
          </DialogContent>

          <DialogActions className="eiDialogActions">
            <Button onClick={() => setShowDatePicker(false)}>{i18n.t('generic.cancel', { defaultValue: 'Anulo' })}</Button>
            <Button onClick={handleClearDateFilter} color="error">
              {i18n.t('buttons.clear')}
            </Button>
            <Button onClick={handleApplyDateFilter} variant="contained">
              {i18n.t('buttons.apply')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Summary Dialog */}
        <Dialog
          open={showSummaryDialog}
          onClose={() => setShowSummaryDialog(false)}
          maxWidth="md"
          fullWidth
          className="eiResponsiveDialog"
          classes={{ paper: 'white-dialog eiResponsiveDialogPaper' }}
        >
          <DialogTitle className="eiDialogTitle">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              <Inventory sx={{ mr: 1, verticalAlign: 'middle' }} />
              {i18n.t('invoice.soldItemsSummary')}
            </Typography>
            <Typography variant="caption" className="eiMuted">
              {i18n.t('invoice.period')}
              {dailySummary?.dateRange}
            </Typography>
          </DialogTitle>

          <DialogContent className="eiDialogBody">
            <div id="daily-summary-content" className="eiSummary eiThermal">
              {/* HEADER (compact) */}
              <div className="eiSummaryHead">
                <Typography className="eiThermalTitle">{i18n.t('invoice.soldItemsSummary')}</Typography>

                <div className="eiThermalMeta">
                  <div>
                    <strong>{i18n.t('invoice.period')}:</strong> {dailySummary?.dateRange}
                  </div>
                  <div>
                    <strong>{i18n.t('invoice.invoices')}:</strong> {dailySummary?.filteredInvoicesCount || 0}
                  </div>
                </div>

                <Divider />
              </div>

              {/* BODY */}
              <div className="eiSummaryBody">
                <TableContainer component={Paper} variant="outlined" className="eiSummaryTable eiThermalTable">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className="eiThermalTh" sx={{ width: 26 }}>
                          #
                        </TableCell>
                        <TableCell className="eiThermalTh">{i18n.t('generic.item')}</TableCell>
                        <TableCell className="eiThermalTh" align="right" sx={{ width: 54 }}>
                          {i18n.t('generic.quantity')}
                        </TableCell>
                        <TableCell className="eiThermalTh" align="right" sx={{ width: 56 }}>
                          {i18n.t('generic.price')}
                        </TableCell>
                        <TableCell className="eiThermalTh" align="right" sx={{ width: 60 }}>
                          {i18n.t('generic.value')}
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {dailySummary?.items?.length ? (
                        <>
                          {dailySummary.items.map((item, index) => (
                            <TableRow key={`${item.productName}-${index}`}>
                              <TableCell className="eiThermalTd">{index + 1}</TableCell>
                              <TableCell className="eiThermalTd eiThermalItem">{item.productName}</TableCell>
                              <TableCell className="eiThermalTd" align="right">
                                {item.quantity}
                              </TableCell>
                              <TableCell className="eiThermalTd" align="right">
                                {formatCurrency(item.unitPrice)}
                              </TableCell>
                              <TableCell className="eiThermalTd eiThermalBold" align="right">
                                {formatCurrency(item.totalValue)}
                              </TableCell>
                            </TableRow>
                          ))}

                          <TableRow className="eiTotalRow">
                            <TableCell className="eiThermalTd eiThermalBold" colSpan={2}>
                              {i18n.t('generic.total')}
                            </TableCell>
                            <TableCell className="eiThermalTd eiThermalBold" align="right">
                              {dailySummary.totals.totalQuantity}
                            </TableCell>
                            <TableCell className="eiThermalTd" align="right">
                              -
                            </TableCell>
                            <TableCell className="eiThermalTd eiThermalBold" align="right">
                              {formatCurrency(dailySummary.totals.totalValue)}
                            </TableCell>
                          </TableRow>

                          {/* footer i vogÃ«l */}
                          <TableRow>
                            <TableCell colSpan={5} className="eiThermalFoot">
                              {dailySummary.dateRange} | {i18n.t('invoice.invoices')}: {dailySummary.filteredInvoicesCount}
                            </TableCell>
                          </TableRow>
                        </>
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 1 }}>
                            <Typography variant="body2" className="eiMuted">
                              {i18n.t('invoice.noItemsForSelectedPeriod')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </div>
          </DialogContent>

          <DialogActions className="eiDialogActions">
            <Button onClick={() => setShowSummaryDialog(false)} startIcon={<Close />}>
              Mbyll
            </Button>

            <Button onClick={downloadSummaryPdf} variant="outlined" startIcon={<Download />}>
              Shkarko(PDF)/printo
            </Button>

            {/* <Button onClick={printSummary} variant="contained" startIcon={<Print />}>
              Printo
            </Button> */}
          </DialogActions>
        </Dialog>

        {/* Content */}
        {loadingInvoices ? (
          <div className="eiCenter eiCard">
            <CircularProgress />
            <Typography className="eiMuted">Duke ngarkuar faturat...</Typography>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="eiEmpty eiCard">
            <Receipt className="eiEmptyIcon" />
            <Typography variant="h5" className="eiEmptyTitle">
              {i18n.t('invoice.noInvoices')}
            </Typography>
            <Typography className="eiMuted" sx={{ textAlign: 'center' }}>
              {dateTo ? i18n.t('invoice.noInvoicesForSelectedPeriod') : i18n.t('invoice.noInvoicesForSelectedUser')}
            </Typography>
          </div>
        ) : (
          <div className="eiGrid">
            {/* Invoices */}
            <div className="eiPanel eiCard">
              <div className="eiPanelHead">
                <div>
                  <div className="eiPanelTitle">
                    {i18n.t('invoice.FilteredInvoices')} ({filteredInvoices.length})
                  </div>
                  <div className="eiPanelSub eiMuted">{dateTo ? `Periudha: ${dateRangeText}` : 'Të gjitha faturat'}</div>
                </div>
              </div>

              <div className="eiPanelBody">
                <TableContainer className="eiTableWrap">
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className="eiTh">{i18n.t('entity.Id')}</TableCell>
                        <TableCell className="eiTh">{i18n.t('entity.Name')}</TableCell>
                        <TableCell className="eiTh">{i18n.t('invoice.Date')}</TableCell>
                        <TableCell className="eiTh">{i18n.t('entity.Price')}</TableCell>
                        <TableCell className="eiTh">{i18n.t('generic.status')}</TableCell>
                        <TableCell className="eiTh">{i18n.t('generic.delivered')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedInvoices.map((inv) => {
                        const status = getStatusInfo(inv.status);
                        return (
                          <TableRow key={inv.id} hover>
                            <TableCell>#{formatInvoiceId(inv.id)}</TableCell>
                            <TableCell>{inv.name}</TableCell>
                            <TableCell>{formatDate(inv.created)}</TableCell>
                            <TableCell>
                              <span className="eiMoney">{formatCurrency(inv.price || 0)}</span> <span className="eiMuted">Lekë</span>
                            </TableCell>
                            <TableCell>
                              <Chip className={`eiChip ${status.color}`} label={status.label} size="small" />
                            </TableCell>
                            <TableCell>
                              {inv.paid === true ? (
                                <Chip className="eiChip closed" label="Dorezuar" size="small" />
                              ) : (
                                <Chip className="eiChip cancelled" label="Jo Dorezuar" size="small" />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredInvoices.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Fatura për faqe:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} nga ${count}`}
                />
              </div>
            </div>

            {/* Articles */}
            <div className="eiPanel eiCard">
              <div className="eiPanelHead">
                <div>
                  <div className="eiPanelTitle">
                    {i18n.t('invoice.soldItems')} ({itemStatistics.length})
                  </div>
                  <div className="eiPanelSub eiMuted">
                    {i18n.t('invoice.period')}: {dateRangeText}
                  </div>
                </div>
              </div>

              <div className="eiPanelBody">
                {itemStatistics.length > 0 ? (
                  <div className="eiMiniTable">
                    <div className="eiMiniHead">
                      <div>#</div>
                      <div>{i18n.t('generic.item')}</div>
                      <div>{i18n.t('generic.quantity')}</div>
                      <div>{i18n.t('generic.value')}</div>
                    </div>

                    {itemStatistics.map((item, idx) => (
                      <div key={item.productId} className="eiMiniRow">
                        <div className={`eiRank ${idx < 3 ? `top-${idx + 1}` : ''}`}>{idx + 1}</div>
                        <div className="eiMiniName" title={item.productName}>
                          {item.productName.length > 28 ? `${item.productName.slice(0, 28)}...` : item.productName}
                        </div>
                        <div className="eiMiniQty">
                          {item.totalQuantity} {i18n.t('measurementUnit.Piece')}
                        </div>
                        <div className="eiMiniVal">
                          <span className="eiMoney">{formatCurrency(item.totalRevenue)}</span> <span className="eiMuted">Lekë</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Typography className="eiMuted" sx={{ textAlign: 'center', py: 4 }}>
                    {i18n.t('invoice.noItemsForSelectedPeriod')}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </BasicCli>
  );
};

export default EmployeeInvoices;
